import React, { useEffect, useState } from 'react';
import { Handle } from 'reactflow';
import { FaEllipsisV } from 'react-icons/fa';
import { IoIosLink } from 'react-icons/io';
import { VscBracketError } from 'react-icons/vsc';
import { TbMathFunction } from 'react-icons/tb';
import { IoCalendarOutline } from 'react-icons/io5';
import { PiMathOperations } from 'react-icons/pi';
import { BsScissors } from 'react-icons/bs';
import { Button, Dialog, DialogActions, DialogTitle, Menu, MenuItem, Tooltip } from '@mui/material';
import { FiType } from 'react-icons/fi';
import { makeStyles } from '@mui/styles';
import TransformationModal from './TransformationModal';

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: '#fff !important',
    color: '#707b7c !important',
    border: '1px solid darkgray',
    fontSize: '15px !important',
    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
  },
  arrow: {
    color: 'darkgray !important',
    // boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
  },
});

function Table2Node({
  data: {
    formField,
    setFormField,
    width,
    label,
    datatype,
    sourceColumns,
    targetColumns,
    reactFlowMapping,
    disableForm,
  },
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const classes = useStyles(); // Hook into the custom styles
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const options = ['Edit Transformation', 'Delete Transformation'];
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const nodeData = formField.mappings
    ?.filter((mapping) => {
      const destinationName = mapping.destination_column?.name;
      return destinationName && label === destinationName && mapping?.transformation?.params;
    })
    ?.map((mapping) => ({
      type: mapping.transformation.type,
      params: mapping.transformation.params,
    }));

  const isTransformationPresent = (formField, label) => {
    return formField.mappings.some((mapping) => mapping.destination_column.name === label && mapping.transformation);
  };

  const handleClick = (event) => {
    isTransformationPresent(formField, label) ? setAnchorEl(event.currentTarget) : setIsModalOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    handleClose();
  };

  const handleTransformation = (event, option) => {
    setAnchorEl(null);
    if (option === 'Edit Transformation') {
      setIsModalOpen(true);
    }
    if (option === 'Delete Transformation') {
      handleOpenDeleteModal();
    }
  };

  const deleteTransformation = (formField, label) => {
    formField.mappings.forEach((mapping) => {
      if (mapping.destination_column.name === label && mapping.transformation) {
        delete mapping.transformation;
      }
    });
    handleCloseDeleteModal();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    // console.log('Table2Node useEffect called');
  }, [formField]);

  // Custom div content for the Tooltip
  // Custom div content for the Tooltip
  const tooltipContent = (
    <div style={{ padding: '8px', minWidth: '150px' }}>
      <strong style={{ color: '#5f6a6a' }}>Transformation</strong>
      <div style={{ margin: '8px 0' }}>
        {nodeData?.length > 0 ? (
          <>
            <div>{nodeData[0].type}</div>
            {Object.entries(nodeData[0].params).map(([key, value], index) => (
              <div key={index}>
                <strong>{`${key.charAt(0).toUpperCase()}${key.slice(1)}`}:</strong> {value}
              </div>
            ))}
          </>
        ) : (
          <div>No Transformations</div>
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        padding: 8,
        background: '#fff',
        border: '1px solid #ccc',
        textAlign: 'left',
        fontWeight: 500,
        width: `${width * 9 + 50}px`,
        margin: '0 0 0 8px',
        fontSize: 9,
      }}
    >
      <Handle type="target" position="left" style={{ background: '#FF5733' }} />

      <Tooltip
        title={tooltipContent}
        placement="right"
        arrow
        classes={{
          tooltip: classes.tooltip, // Custom tooltip styles
          arrow: classes.arrow, // Custom arrow styles
        }}
      >
        <span>
          {label} <span style={{ fontWeight: 600, marginLeft: '2px' }}>[{datatype}]</span>
        </span>
      </Tooltip>

      {formField?.mappings?.map((mapping, index) => {
        const destinationName = mapping.destination_column?.name;

        if (destinationName && label === destinationName && mapping?.transformation?.params) {
          const iconStyle = { position: 'absolute', top: '8px', right: '30px', fontSize: 12, color: '#0d6fd1' };
          const transformationIcons = {
            Concatenation: <IoIosLink style={iconStyle} />,
            ArithmeticOperation: <PiMathOperations style={iconStyle} />,
            Substring: <BsScissors style={iconStyle} />,
            DateTimeFormatting: <IoCalendarOutline style={iconStyle} />,
            ConditionalFormatting: <TbMathFunction style={iconStyle} />,
            NullHandling: <VscBracketError style={iconStyle} />,
            DataTypeConversion: <FiType style={iconStyle} />,
          };

          const icon = transformationIcons[mapping?.transformation?.type];

          if (icon) {
            return icon;
          }
        }

        return null;
      })}
      <Button
        style={{
          position: 'absolute',
          top: 8,
          right: -12,
          fontSize: 12,
          color: 'gray',
          cursor: 'pointer',
          background: 'transparent',
          border: 'none',
          padding: 0,
          outline: 'none',
        }}
        onClick={handleClick}
        aria-label="options"
        disabled={disableForm}
      >
        <FaEllipsisV />
      </Button>

      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {options.map((option) => (
          <MenuItem key={option} onClick={(e) => handleTransformation(e, option)}>
            {option}
          </MenuItem>
        ))}
      </Menu>
      {isModalOpen && (
        <TransformationModal
          data={{ formField, setFormField, sourceColumns, targetColumns, reactFlowMapping }}
          isModalOpen={isModalOpen}
          handleCloseModal={handleCloseModal}
          label={label}
        />
      )}
      {openDeleteModal && (
        <Dialog
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{`Are you sure you want to delete?`}</DialogTitle>
          <DialogActions sx={{ justifyContent: 'center', marginBottom: '10px', marginTop: '-10px' }}>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteModal}
              sx={{ textTransform: 'capitalize', color: '#43425D', border: '1px solid #43425D' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => deleteTransformation(formField, label)}
              autoFocus
              sx={{
                backgroundColor: '#00043C',
                textTransform: 'capitalize',
                '&:hover': { backgroundColor: '#00043C' },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default Table2Node;
